/**
 * 2026-10-30 additions, shared by the workers and the three frontends (request bodies are in
 * growth-schemas.ts): plans and modules, platform admin roles, audit, two-step sign-in, SMS login,
 * segments, automatic reminders, the member inbox, member referrals, shops, campaigns (campaigns.ts),
 * analytics, coupon code batches, paid membership, stored value and gift cards.
 *
 * Most of the member-facing programme features need the platform's own loyalty engine
 * (`dataSource: 'native'`); PROAN brands keep points, levels and coupons in their POS.
 */
import type { LocalizedText } from './i18n.ts';
import type { ConsentChannel, DataSource, EarnRatio, MemberRow, Page } from './contract.ts';
import type { AppliedCampaign } from './campaigns.ts';

// ---------------------------------------------------------------- plans and modules

/** Paid features a brand gets from its plan, or as an add-on assigned in the admin portal. */
export const MODULES = [
  'segments',
  'automations',
  'referralRewards',
  'pointsPromotions',
  'analytics',
  'codeBatches',
  'shopScoping',
  'smsLogin',
  'paidMembership',
  'storedValue',
  'giftCards',
  'newsletter',
  'aiImages',
  // AI add-ons (31/10): one per AI function, see AI_FUNCTION_INFO in ai.ts.
  'aiCopy',
  'aiTranslate',
  'aiCampaignPlanner',
  'aiSegments',
  'aiMemberValue',
  'aiWeeklyReport',
  'aiCampaignReview',
  'aiOnboarding',
  'aiForecast',
] as const;
export type ModuleKey = (typeof MODULES)[number];

/** Optional upgrades: never on by default, the platform assigns them brand by brand (or puts them in a plan). */
export const ADDON_MODULES: readonly ModuleKey[] = [
  'paidMembership',
  'storedValue',
  'giftCards',
  'newsletter',
  'aiImages',
  'aiCopy',
  'aiTranslate',
  'aiCampaignPlanner',
  'aiSegments',
  'aiMemberValue',
  'aiWeeklyReport',
  'aiCampaignReview',
  'aiOnboarding',
  'aiForecast',
];

/** Need the platform's own loyalty engine; switched off for PROAN brands whatever the plan says. */
export const NATIVE_ONLY_MODULES: readonly ModuleKey[] = [
  'automations',
  'referralRewards',
  'pointsPromotions',
  'codeBatches',
  'paidMembership',
  'storedValue',
  'giftCards',
  // They read the sales and campaigns recorded in this platform.
  'aiCampaignPlanner',
  'aiMemberValue',
  'aiCampaignReview',
  'aiForecast',
];

export const MODULE_LABELS: Record<ModuleKey, string> = {
  segments: '會員分群',
  automations: '自動提醒',
  referralRewards: '推薦獎賞',
  pointsPromotions: '推廣活動',
  analytics: '進階分析',
  codeBatches: '批量優惠碼',
  shopScoping: '分店權限及分店報表',
  smsLogin: '手機短訊登入',
  paidMembership: '付費會籍',
  storedValue: '儲值',
  giftCards: '電子禮券',
  newsletter: '電子報群發',
  aiImages: 'AI 生成圖片',
  aiCopy: 'AI 文案助手',
  aiTranslate: 'AI 三語翻譯',
  aiCampaignPlanner: 'AI 推廣活動企劃',
  aiSegments: 'AI 一句話分群',
  aiMemberValue: 'AI 會員價值預測',
  aiWeeklyReport: 'AI 每週營運周報',
  aiCampaignReview: 'AI 推廣成效分析',
  aiOnboarding: 'AI 開店助手',
  aiForecast: 'AI 客流及銷售預測',
};

/** null = no limit. */
export interface PlanLimits {
  /** Soft: registration keeps working, the brand is flagged as over its plan. */
  members: number | null;
  /** Hard: 分店 beyond this cannot be saved. */
  shops: number | null;
  /** Hard: brand portal accounts (owner + staff) beyond this cannot be invited. */
  staff: number | null;
  /** Hard: SMS (login codes) per calendar month; at the limit members are asked to use email instead. */
  smsPerMonth: number | null;
  /** Hard: AI images per calendar month (aiImages add-on). */
  aiImagesPerMonth: number | null;
  /** Hard: newsletter emails per calendar month (newsletter add-on), plus that month's 加額; test emails are not counted. */
  newsletterEmailsPerMonth: number | null;
}
export const PLAN_LIMIT_KEYS = ['members', 'shops', 'staff', 'smsPerMonth', 'aiImagesPerMonth', 'newsletterEmailsPerMonth'] as const satisfies ReadonlyArray<keyof PlanLimits>;
export const NO_LIMITS: PlanLimits = { members: null, shops: null, staff: null, smsPerMonth: null, aiImagesPerMonth: null, newsletterEmailsPerMonth: null };

/** 計劃 in the admin portal. Billing itself is offline; monthlyFee is for reference. */
export interface PlanRow {
  id: string;
  name: string;
  description: string;
  /** HKD per month, for reference. */
  monthlyFee: number | null;
  modules: ModuleKey[];
  limits: PlanLimits;
  sort: number;
  brandCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface BrandUsage {
  members: number;
  shops: number;
  staff: number;
  /** SMS sent this calendar month (Hong Kong time). */
  smsThisMonth: number;
  /** AI images made this calendar month (Hong Kong time). */
  aiImagesThisMonth: number;
  /** Newsletter emails sent this calendar month (Hong Kong time). */
  newsletterEmailsThisMonth: number;
  /** 加額 for this month: newsletter emails on top of newsletterEmailsPerMonth, added in the admin portal. */
  newsletterTopUpThisMonth: number;
}

/** What a brand can use right now. */
export interface BrandEntitlements {
  /** null = no plan assigned yet: every standard module on and no limits (add-ons still need assigning). */
  planId: string | null;
  planName: string | null;
  /** Effective modules: plan modules (all standard ones when there is no plan) + add-ons, minus native-only ones for PROAN brands. */
  modules: ModuleKey[];
  /** Add-ons assigned to this brand on top of its plan. */
  extraModules: ModuleKey[];
  /** Effective limits: the plan's, with this brand's overrides on top. */
  limits: PlanLimits;
  /** This brand's own overrides (absent key = use the plan's). */
  limitOverrides: Partial<PlanLimits>;
  usage: BrandUsage;
  /** Limits currently reached or passed. */
  over: Array<keyof PlanLimits>;
}

/**
 * Effective modules of a brand. `planModules` null = no plan (every standard module). Shared so the
 * admin API and the brand API agree.
 */
export function effectiveModules(planModules: readonly ModuleKey[] | null, extra: readonly ModuleKey[], dataSource: DataSource): ModuleKey[] {
  const base = planModules ?? MODULES.filter((m) => !ADDON_MODULES.includes(m));
  const set = new Set<ModuleKey>([...base, ...extra]);
  return MODULES.filter((m) => set.has(m) && (dataSource === 'native' || !NATIVE_ONLY_MODULES.includes(m)));
}

export function effectiveLimits(plan: PlanLimits | null, overrides: Partial<PlanLimits>): PlanLimits {
  const base = plan ?? NO_LIMITS;
  return {
    members: overrides.members !== undefined ? overrides.members : base.members,
    shops: overrides.shops !== undefined ? overrides.shops : base.shops,
    staff: overrides.staff !== undefined ? overrides.staff : base.staff,
    smsPerMonth: overrides.smsPerMonth !== undefined ? overrides.smsPerMonth : base.smsPerMonth,
    // Plans saved before this limit existed have no key: no limit.
    aiImagesPerMonth: overrides.aiImagesPerMonth !== undefined ? overrides.aiImagesPerMonth : (base.aiImagesPerMonth ?? null),
    newsletterEmailsPerMonth: overrides.newsletterEmailsPerMonth !== undefined ? overrides.newsletterEmailsPerMonth : (base.newsletterEmailsPerMonth ?? null),
  };
}

/** Limits reached (members: passed). */
export function overLimits(limits: PlanLimits, usage: BrandUsage): Array<keyof PlanLimits> {
  const out: Array<keyof PlanLimits> = [];
  if (limits.members !== null && usage.members > limits.members) out.push('members');
  if (limits.shops !== null && usage.shops >= limits.shops) out.push('shops');
  if (limits.staff !== null && usage.staff >= limits.staff) out.push('staff');
  if (limits.smsPerMonth !== null && usage.smsThisMonth >= limits.smsPerMonth) out.push('smsPerMonth');
  if (limits.aiImagesPerMonth !== null && usage.aiImagesThisMonth >= limits.aiImagesPerMonth) out.push('aiImagesPerMonth');
  if (limits.newsletterEmailsPerMonth !== null && usage.newsletterEmailsThisMonth >= limits.newsletterEmailsPerMonth + usage.newsletterTopUpThisMonth) out.push('newsletterEmailsPerMonth');
  return out;
}

// ---------------------------------------------------------------- platform admin roles

export const ADMIN_ROLES = ['super', 'operator', 'support', 'finance'] as const;
export type AdminRole = (typeof ADMIN_ROLES)[number];
export const ADMIN_ROLE_LABELS: Record<AdminRole, string> = { super: '最高權限', operator: '營運', support: '客戶支援', finance: '財務' };

export const ADMIN_PERMISSIONS = [
  /** See brands, their users, domains, plan and history. */
  'brands.read',
  /** Create / edit brands, PROAN settings, domains, status, plan assignment, brand users and invites. */
  'brands.write',
  /** Open the brand portal ("log in to the merchant portal"). */
  'brands.portal',
  /** Change things inside the brand portal; without it the brand portal is read-only. */
  'brands.portalWrite',
  /** Resend invites and set passwords / reset two-step sign-in of brand users. */
  'users.manage',
  /** Platform admins and their roles. */
  'admins.manage',
  /** Plan definitions. */
  'plans.manage',
  /** SMS gateway settings and SMS log. */
  'sms.manage',
  /** 「Google 登入」: the platform's Google OAuth client for member sign-in. */
  'login.manage',
  /** 「AI 設定」 (API key, models). */
  'ai.manage',
  /** 「AI 用量」 dashboard. */
  'ai.read',
  'audit.read',
  'reports.read',
] as const;
export type AdminPermission = (typeof ADMIN_PERMISSIONS)[number];

export const ROLE_PERMISSIONS: Record<AdminRole, readonly AdminPermission[]> = {
  super: ADMIN_PERMISSIONS,
  operator: ['brands.read', 'brands.write', 'brands.portal', 'brands.portalWrite', 'users.manage', 'audit.read', 'reports.read', 'ai.read'],
  support: ['brands.read', 'brands.portal', 'users.manage', 'audit.read'],
  finance: ['brands.read', 'audit.read', 'reports.read', 'ai.read'],
};

/** Platform admins created before roles existed (role null) have every permission. */
export function adminCan(role: AdminRole | null, permission: AdminPermission): boolean {
  return ROLE_PERMISSIONS[role ?? 'super'].includes(permission);
}

// ---------------------------------------------------------------- audit

/** 操作紀錄 (admin portal): platform admins' actions, including what they changed in a brand portal. */
export interface AuditRow {
  id: string;
  createdAt: string;
  actorEmail: string | null;
  action: string;
  brandId: string | null;
  brandName: string | null;
  detail: Record<string, unknown>;
  ip: string | null;
}

/** A brand's own log (owner / staff / admins in its brand portal), read through the admin portal. */
export interface BrandAuditRow {
  id: string;
  createdAt: string;
  actor: string;
  action: string;
  detail: Record<string, unknown> | null;
}

// ---------------------------------------------------------------- two-step sign-in (TOTP)

/** Step 1 of turning on two-step sign-in: scan otpauthUrl as a QR code, then confirm a code with totpEnableSchema. */
export interface TotpSetup {
  secret: string;
  otpauthUrl: string;
}

// ---------------------------------------------------------------- SMS

export type SmsProvider = 'off' | 'test' | 'twilio';

/**
 * Platform SMS gateway (admin portal 短訊設定). test = nothing is sent; messages, codes included,
 * appear in the admin portal's SMS log (for QA only).
 */
/**
 * 「Google 登入」 (admin portal): one Google OAuth client for every member site. Google sends members
 * back to `redirectUri` on the platform's own host, which hands them on to the brand's site.
 */
export interface GoogleLoginConfigMasked {
  enabled: boolean;
  clientId: string | null;
  hasClientSecret: boolean;
  /** The 已授權的重新導向 URI to enter in Google Cloud Console. */
  redirectUri: string;
  /** On, with a client ID and secret: brands that leave it switched on show the button. */
  ready: boolean;
}

export interface SmsConfigMasked {
  provider: SmsProvider;
  twilioAccountSid: string | null;
  hasTwilioToken: boolean;
  /** Twilio "From": an alphanumeric sender ID (max 11 characters) or a Twilio number. */
  sender: string;
  /** Country calling codes members may use, e.g. ["852", "853"]. */
  allowedCountryCodes: string[];
  /** Platform-wide safety cap per Hong Kong day. */
  dailyCap: number;
  sentToday: number;
}

export interface SmsLogRow {
  id: string;
  createdAt: string;
  brandId: string | null;
  brandName: string | null;
  /** Masked (+852 ****5678) except in test mode. */
  to: string;
  /** Full text in test mode; the code is masked for real messages. */
  body: string;
  purpose: 'login' | 'register' | 'test';
  provider: SmsProvider;
  status: 'sent' | 'failed' | 'test';
  error: string | null;
}

export interface SmsSendResult {
  /** "+852 ****5678" */
  sentTo: string;
  /** Wait this long before asking for another code. */
  resendAfterSec: number;
  /** The platform is in test mode: no real SMS; QA reads the code from the admin portal's SMS log. */
  testMode: boolean;
}

// ---------------------------------------------------------------- segments

export const RFM_SEGMENTS = ['champions', 'loyal', 'potential', 'new', 'at_risk', 'hibernating', 'lost'] as const;
export type RfmSegment = (typeof RFM_SEGMENTS)[number];
/**
 * Native brands, by last purchase (recency) and purchases in the last 365 days (frequency):
 * champions ≤30 days and ≥5; loyal ≤90 days and ≥3; new = joined ≤30 days ago, ≤1 purchase;
 * potential = other ≤90 days; at_risk 91–180 days and ≥3; hibernating = other 91–365 days;
 * lost = over 365 days, or never bought and joined over 30 days ago. First match wins in this order:
 * new, champions, loyal, potential, at_risk, hibernating, lost.
 */
export const RFM_LABELS: Record<RfmSegment, string> = {
  champions: '最佳顧客',
  loyal: '忠實顧客',
  potential: '有潛力',
  new: '新會員',
  at_risk: '流失風險',
  hibernating: '沉睡',
  lost: '已流失',
};

/** Every condition set must match (AND). Empty rules = every active member. */
export interface SegmentRules {
  /** Native: current level. */
  tierIds?: number[];
  /** Joined on or after / on or before (Hong Kong dates YYYY-MM-DD). */
  joinedFrom?: string | null;
  joinedTo?: string | null;
  /** Native: bought within the last N days. */
  purchasedWithinDays?: number | null;
  /** Native: no purchase for at least N days (members who never bought count, by join date). */
  noPurchaseForDays?: number | null;
  /** Native: lifetime spend (HKD). */
  spendMin?: number | null;
  spendMax?: number | null;
  /** Native: purchases in the last 365 days. */
  visitsMin?: number | null;
  /** Native: current points. */
  pointsMin?: number | null;
  pointsMax?: number | null;
  /** 1–12 */
  birthdayMonths?: number[];
  /** Native: bought at any of these shops (StoreInfo.id). */
  shopIds?: string[];
  /** How they joined: a referral link code, 'member' (referred by a member) or 'direct'. */
  sources?: string[];
  /** Only members who agreed to this marketing channel. */
  consent?: ConsentChannel | null;
  gender?: 'M' | 'F' | null;
  /** Native only. */
  rfm?: RfmSegment[];
  /** Native: bought a product (LPSS-43). */
  purchase?: SegmentPurchaseRule | null;
}

/**
 * 購買產品: more than `moreThan` units of `sku` (summed over the member's sales) at any of these shops in the
 * last `within` days / months. Reversed sales are gone from the ledger, so they never count.
 */
export interface SegmentPurchaseRule {
  shopIds: string[];
  within: number;
  unit: 'day' | 'month';
  sku: string;
  moreThan: number;
}

/**
 * daily = 每日更新: the member list is made again once a Hong Kong day (and when the rules change).
 * snapshot = 歷史快照: the list is fixed when it is made; a new snapshot is made only on request (or
 * when the rules change). Member list, export, Wallet push and coupons use the current list.
 */
export const SEGMENT_KINDS = ['daily', 'snapshot'] as const;
export type SegmentKind = (typeof SEGMENT_KINDS)[number];

export interface SegmentAdmin {
  id: string;
  name: string;
  description: string;
  kind: SegmentKind;
  rules: SegmentRules;
  /** Members in the current list (today's list, or the latest snapshot). */
  memberCount: number;
  /** When the current list was made. */
  listAt: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/** One fixed member list of a 歷史快照 segment. */
export interface SegmentSnapshot {
  id: string;
  createdAt: string;
  createdBy: string;
  memberCount: number;
}

/** 報表 (summary) or 進階分析 (analytics) figures saved as they were at createdAt. */
export const REPORT_KINDS = ['summary', 'analytics'] as const;
export type ReportKind = (typeof REPORT_KINDS)[number];

export interface ReportSnapshotMeta {
  id: string;
  kind: ReportKind;
  title: string;
  from: string;
  to: string;
  shopId: string | null;
  createdBy: string;
  createdAt: string;
}

export interface ReportSnapshot<T = unknown> extends ReportSnapshotMeta {
  data: T;
}

export interface SegmentPreview {
  count: number;
  /** First 10 matching members. */
  sample: MemberRow[];
}

export interface SegmentCouponResult {
  issued: number;
  /** Members over the coupon's per-member limit, or the coupon's total limit was reached. */
  skipped: number;
}

// ---------------------------------------------------------------- automatic reminders (settings section `automations`)

export const AUTOMATION_KINDS = ['pointsExpiry', 'inactive', 'tierUpgrade', 'tierExpiry', 'couponExpiry'] as const;
export type AutomationKind = (typeof AUTOMATION_KINDS)[number];
export const AUTOMATION_LABELS: Record<AutomationKind, string> = {
  pointsExpiry: '積分到期提醒',
  inactive: '久未光顧挽回',
  tierUpgrade: '升級通知',
  tierExpiry: '等級到期提醒',
  couponExpiry: '優惠券到期提醒',
};

/**
 * One reminder. It always lands in the member's 訊息中心 (inbox); `email` also emails it. Text may use
 * {name} {points} {date} {level} {days} {coupon}. Optional gift (coupon and/or points) with it.
 * `inactive` is marketing: it only goes to members who agreed to email (for the email) — the inbox copy
 * goes to everyone. The others are notices about the member's own account.
 */
export interface AutomationRule {
  enabled: boolean;
  email: boolean;
  title: LocalizedText;
  body: LocalizedText;
  rewardId: string | null;
  points: number;
}

export interface AutomationSettings {
  /** Before the yearly points clearing (programme setting): e.g. [30, 7] days before. Members with 0 points are skipped. */
  pointsExpiry: AutomationRule & { daysBefore: number[] };
  /** No purchase for `days` (never bought: since joining). Once per quiet spell. */
  inactive: AutomationRule & { days: number };
  /** Right after a sale moves the member up. */
  tierUpgrade: AutomationRule;
  /** `daysBefore` the level ends while the keep-level spend is not reached yet. */
  tierExpiry: AutomationRule & { daysBefore: number };
  /** `daysBefore` an unused coupon expires. */
  couponExpiry: AutomationRule & { daysBefore: number };
}

export interface AutomationLogRow {
  id: string;
  createdAt: string;
  kind: AutomationKind;
  memberId: string;
  memberNo: string;
  memberName: string;
  /** 'inbox' always; 'email' when emailed. */
  channels: Array<'inbox' | 'email'>;
  couponTitle: LocalizedText | null;
  points: number;
}

export interface AutomationRunResult {
  sent: Record<AutomationKind, number>;
}

// ---------------------------------------------------------------- member inbox (訊息中心)

export type InboxKind = 'message' | 'reminder' | 'referral' | 'level' | 'coupon' | 'stored_value';

export interface InboxItem {
  id: string;
  kind: InboxKind;
  /** Already in the member's language. */
  title: string;
  body: string;
  /** Path on the member site to open, e.g. "/member/coupons". */
  link: string | null;
  read: boolean;
  createdAt: string;
}

export interface InboxPage extends Page<InboxItem> {
  unread: number;
}

// ---------------------------------------------------------------- member referrals (settings section `referral`)

export interface ReferralSettings {
  enabled: boolean;
  /** When both sides get their gift: on sign-up, or on the new member's first purchase. */
  trigger: 'register' | 'first_purchase';
  referrer: { points: number; rewardId: string | null };
  referee: { points: number; rewardId: string | null };
  /** Gifts per referrer per calendar month; null = no cap (the new member still gets theirs). */
  monthlyCap: number | null;
  terms: LocalizedText;
}

export type ReferralStatus = 'pending' | 'rewarded' | 'rejected';
/** self = same email / phone as the referrer; duplicate = phone already used by another member; cap = referrer over the monthly cap. */
export type ReferralRejectReason = 'self' | 'duplicate' | 'cap';

export interface ReferralRow {
  id: string;
  createdAt: string;
  referrerId: string;
  referrerNo: string;
  referrerName: string;
  refereeId: string;
  refereeNo: string;
  refereeName: string;
  status: ReferralStatus;
  reason: ReferralRejectReason | null;
  rewardedAt: string | null;
}

export interface ReferralPage extends Page<ReferralRow> {
  summary: { total: number; rewarded: number; pending: number; rejected: number };
}

/** 推薦朋友 page on the member site. */
export interface MemberReferral {
  enabled: boolean;
  code: string;
  /** Link to share: the member site's register page with ?ref=code (the code is filled in). */
  url: string;
  trigger: ReferralSettings['trigger'];
  referrerGift: { points: number; couponTitle: LocalizedText | null };
  refereeGift: { points: number; couponTitle: LocalizedText | null };
  terms: LocalizedText;
  invited: number;
  rewarded: number;
  pending: number;
  /** Latest 20, names masked (陳*明). */
  recent: Array<{ name: string; status: ReferralStatus; createdAt: string }>;
}

// ---------------------------------------------------------------- shops

/** 分店報表 (native brands). Sales without a shop are under shopId null. */
export interface ShopReportRow {
  shopId: string | null;
  name: LocalizedText | null;
  sales: number;
  amount: number;
  points: number;
  /** Different members who bought there. */
  members: number;
  couponsUsed: number;
}

// ---------------------------------------------------------------- sales (native)

/** What a sale would give, before saving it. */
export interface PurchasePreview {
  points: number;
  /** Points without any campaign. */
  basePoints: number;
  stamps: number;
  /** 推廣活動 the sale would get something from. */
  campaigns: AppliedCampaign[];
  levelChange: { from: LocalizedText | null; to: LocalizedText } | null;
}

// ---------------------------------------------------------------- analytics

export interface BrandAnalytics {
  from: string;
  to: string;
  dataSource: DataSource;
  members: { total: number; newInRange: number; active30: number; active90: number; inactive90: number };
  /** The rest is null for PROAN brands (sales live in the POS). */
  sales: { count: number; amount: number; points: number; avgAmount: number; buyers: number } | null;
  /** Share (0–1) of buyers in the range who bought at least twice in it. */
  repeatRate: number | null;
  salesDaily: Array<{ day: string; count: number; amount: number }> | null;
  rfm: Array<{ segment: RfmSegment; count: number; spend: number }> | null;
  /** Members by the level they are shown on: a 付費會籍 (tierId 0, paidPlanId set) counts instead of their spend level. */
  tiers: Array<{ tierId: number; paidPlanId?: string; name: LocalizedText; color: string; count: number }> | null;
  /** Points: outstanding now; issued / redeemed / expired in the range. */
  points: { outstanding: number; issued: number; redeemed: number; expired: number } | null;
  coupons: Array<{ rewardId: string; title: LocalizedText; issued: number; used: number; expired: number }> | null;
  shops: ShopReportRow[] | null;
  /** Stored value module only. */
  storedValue: { outstanding: number; toppedUp: number; bonus: number; spent: number } | null;
  referrals: { invited: number; rewarded: number } | null;
  /** Members by how they joined (all sources). */
  sources: Array<{ source: string; count: number }>;
}

// ---------------------------------------------------------------- coupon code batches (native)

export interface CodeBatchAdmin {
  id: string;
  name: string;
  rewardId: string;
  rewardTitle: LocalizedText;
  quantity: number;
  prefix: string;
  /** ISO times; null = open-ended. */
  claimStart: string | null;
  claimEnd: string | null;
  /** Codes of this batch one member may claim; null = no limit. */
  perMemberLimit: number | null;
  claimed: number;
  used: number;
  voided: number;
  createdBy: string;
  createdAt: string;
}

/**
 * unclaimed = not used yet; claimed = a member added it to 我的優惠券 (it is then their coupon, used
 * with the coupon code); used = accepted at the counter without a member; void = cancelled.
 */
export type CodeStatus = 'unclaimed' | 'claimed' | 'used' | 'void';

export interface CodeRow {
  code: string;
  status: CodeStatus;
  memberNo: string | null;
  memberName: string | null;
  claimedAt: string | null;
  usedAt: string | null;
}

/** 公開優惠碼 (native): one shared code; used = members who added it to 我的優惠券. */
export interface PublicCodeAdmin {
  code: string;
  rewardId: string;
  rewardTitle: LocalizedText;
  startAt: string | null;
  endAt: string;
  limit: number;
  used: number;
  active: boolean;
  createdBy: string;
  createdAt: string;
}

// ---------------------------------------------------------------- payments taken at the counter (add-ons)

export const PAYMENT_METHODS = ['cash', 'card', 'fps', 'payme', 'alipay', 'wechat', 'octopus', 'other'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];
export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  cash: '現金',
  card: '信用卡',
  fps: '轉數快',
  payme: 'PayMe',
  alipay: 'AlipayHK',
  wechat: 'WeChat Pay',
  octopus: '八達通',
  other: '其他',
};

// ---------------------------------------------------------------- paid membership (add-on, native)

/**
 * LPSS-44: a 付費會籍 is its own level, on top of the member's spend level. How it lasts: 每月續訂 (each
 * purchase adds a month), 限期購買 (each purchase adds `months`), 永久購買 (no end).
 */
export const PAID_TERMS = ['monthly', 'fixed', 'permanent'] as const;
export type PaidTerm = (typeof PAID_TERMS)[number];
export const PAID_TERM_LABELS: Record<PaidTerm, string> = { monthly: '每月續訂', fixed: '限期購買', permanent: '永久購買' };
/** points = a 積分 ratio of its own; discount = a fixed 折扣 (stored, shown; the shop applies it). */
export type PaidBenefit = 'points' | 'discount';
/** A 折扣 as written in Chinese: 80 → "8", 85 → "85", 90 → "9" (as in 8 折, 85 折, 9 折). */
export const discountZhe = (pct: number) => String(pct % 10 === 0 ? pct / 10 : pct);

export interface PaidPlanAdmin {
  id: string;
  name: LocalizedText;
  description: LocalizedText;
  color: string;
  /** A recorded sale (POS or counter) with this product code buys the level; null = sold at the counter only. */
  sku: string | null;
  /** HKD */
  price: number;
  term: PaidTerm;
  /** 限期購買: months each purchase adds. */
  months: number;
  benefit: PaidBenefit;
  /** benefit 'points': what the member earns while on the level. */
  earn: EarnRatio | null;
  /** benefit 'discount': percent of the price paid, e.g. 80 = 8 折. */
  discount: number | null;
  bonusPoints: number;
  rewardId: string | null;
  active: boolean;
  sort: number;
  soldCount: number;
}

export interface PaidPlanPublic {
  id: string;
  name: LocalizedText;
  description: LocalizedText;
  price: number;
  color: string;
  term: PaidTerm;
  months: number;
  benefit: PaidBenefit;
  earn: EarnRatio | null;
  discount: number | null;
  bonusPoints: number;
  couponTitle: LocalizedText | null;
}

export type PaidMembershipStatus = 'active' | 'expired' | 'cancelled';

export interface PaidMembershipRow {
  id: string;
  createdAt: string;
  memberId: string;
  memberNo: string;
  memberName: string;
  planId: string;
  planName: LocalizedText;
  price: number;
  paymentMethod: PaymentMethod;
  paymentRef: string | null;
  /** Hong Kong dates, inclusive; ends null = 永久購買. */
  starts: string;
  ends: string | null;
  status: PaidMembershipStatus;
  soldBy: string;
}

/** The member's 付費會籍 now (the level they are shown on). */
export interface MemberPaidMembership {
  planName: LocalizedText;
  term: PaidTerm;
  benefit: PaidBenefit;
  earn: EarnRatio | null;
  discount: number | null;
  starts: string;
  /** Last day; null = 永久購買. */
  ends: string | null;
}

// ---------------------------------------------------------------- stored value (add-on, native; settings section `storedValue`)

/**
 * Balance in HKD a member tops up at the counter and spends at the brand's own shops only. Not
 * withdrawable as cash. (Single-purpose stored value; whether a licence is needed is for the brand's
 * own legal advice.)
 */
export interface StoredValueSettings {
  /**
   * Top up at least `topup` and get `bonus` extra, e.g. 1000 → 100. The highest rule the top-up reaches applies:
   * once (只觸發一次), or with `repeat` (可重複觸發, LPSS-48) once for every full `topup` in it. Rows saved before: once.
   */
  bonusRules: Array<{ topup: number; bonus: number; repeat: boolean }>;
  minTopup: number;
  /** Largest balance a member may hold; null = no cap. */
  maxBalance: number | null;
  /**
   * 儲值有效期（日）(LPSS-46): each 增值, with its 增值贈送, made on day 0 can be used through day N and comes off on
   * day N + 1. null or 0 = no expiry. The setting of the day decides (clearing it keeps older 增值).
   */
  validDays: number | null;
  /** 儲值 SKU (LPSS-47): a recorded sale's line of a listed SKU credits quantity × amount as a 增值, without 增值優惠. */
  skus: Array<{ sku: string; amount: number }>;
  terms: LocalizedText;
}

/** Bonus for a top-up of `amount` (StoredValueSettings.bonusRules), exact to the cent. */
export function topupBonus(rules: StoredValueSettings['bonusRules'], amount: number): number {
  let best: StoredValueSettings['bonusRules'][number] | null = null;
  for (const r of rules) if (amount >= r.topup && (!best || r.topup > best.topup)) best = r;
  if (!best) return 0;
  const times = best.repeat ? Math.floor(Math.round(amount * 100) / Math.round(best.topup * 100)) : 1;
  return Math.round(times * best.bonus * 100) / 100;
}

/** 'expire' = a 增值 past its 儲值有效期 (LPSS-46). */
export type StoredValueKind = 'topup' | 'bonus' | 'spend' | 'gift_card' | 'adjust' | 'refund' | 'expire';

export interface StoredValueEntry {
  id: string;
  createdAt: string;
  kind: StoredValueKind;
  /** + added, − taken. */
  amount: number;
  /** Balance after this entry. */
  balance: number;
  paymentMethod: PaymentMethod | null;
  paymentRef: string | null;
  invoiceNo: string | null;
  shop: string | null;
  note: string | null;
  /** Brand portal only (null on the member site). */
  actor: string | null;
}

/** 儲值記錄 on the member site, with the 增值 that ends first (LPSS-46): its last day and what is left; null = none ends. */
export interface MemberStoredValuePage extends Page<StoredValueEntry> {
  nextExpiry: { day: string; amount: number } | null;
}

/** 儲值記錄 in the brand portal (every member). */
export interface StoredValueRow extends StoredValueEntry {
  memberId: string;
  memberNo: string;
  memberName: string;
}

export interface StoredValuePage extends Page<StoredValueRow> {
  /** Sum of every member's balance now. */
  outstanding: number;
  /** Over the filter. */
  toppedUp: number;
  bonus: number;
  spent: number;
}

// ---------------------------------------------------------------- gift cards (add-on, native)

export interface GiftCardProductAdmin {
  id: string;
  name: LocalizedText;
  /** Value added to the balance when redeemed (HKD). */
  amount: number;
  /** Selling price (HKD), may be below `amount`. */
  price: number;
  /** Months a sold card stays redeemable; null = no expiry. */
  validMonths: number | null;
  active: boolean;
  sort: number;
  soldCount: number;
}

export type GiftCardProductPublic = Omit<GiftCardProductAdmin, 'active' | 'sort' | 'soldCount'>;

export type GiftCardStatus = 'active' | 'redeemed' | 'expired' | 'void';

export interface GiftCardRow {
  /** 12 characters, shown as XXXX-XXXX-XXXX. */
  code: string;
  productId: string;
  productName: LocalizedText;
  amount: number;
  price: number;
  status: GiftCardStatus;
  buyerName: string | null;
  buyerPhone: string | null;
  paymentMethod: PaymentMethod;
  paymentRef: string | null;
  soldBy: string;
  soldAt: string;
  validTo: string | null;
  /** Link on the member site to send to the recipient (/gift/CODE). */
  shareUrl: string;
  redeemedMemberNo: string | null;
  redeemedMemberName: string | null;
  redeemedAt: string | null;
}

/** /gift/:code on the member site (anyone with the link). */
export interface GiftCardPublic {
  productName: LocalizedText;
  amount: number;
  status: GiftCardStatus;
  validTo: string | null;
}

export interface GiftCardRedeemResult {
  amount: number;
  balance: number;
}

/** Paying with the balance; `sale` is set when the payment was also recorded as a sale (points, stamps, level). */
export interface StoredValueSpendResult {
  sale: { points: number; stamps: number; levelChanged: { from: LocalizedText | null; to: LocalizedText } | null; campaigns: AppliedCampaign[] } | null;
  detail: import('./contract.ts').MemberLoyaltyDetail;
}
